import {
  IconDefinition,
  faCircleCheck,
  faDollarSign,
  faFileCheck,
  faFileImport,
  faListCheck,
  faLoader,
  faSignature,
  faStamp,
  faTimer,
  faUserCheck,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { BorrowerDtoStatusEnum } from '../../../openapi/atlantis';
import { EnumMap } from '../../../types';
import { capitalizeEnum, safeEnumMapGet } from '../../../utils/EnumHelper';
import { cn } from '../../../utils/classUtils';
import { LoanStatusProps } from '../LoanStatus';

export const variantToTextClassNameMap: EnumMap<BorrowerDtoStatusEnum, string> =
  {
    [BorrowerDtoStatusEnum.AccountCreated]: 'text-reskin-yellow-900',
    [BorrowerDtoStatusEnum.ApplicationInProgress]: 'text-reskin-orange-600',
    [BorrowerDtoStatusEnum.ApplicationSubmitted]: 'text-reskin-purple-600',
    [BorrowerDtoStatusEnum.LoanPreApproved]: 'text-reskin-green-900',
    [BorrowerDtoStatusEnum.SubmittedToLender]: 'text-primary-navy',
    [BorrowerDtoStatusEnum.Underwriting]: 'text-reskin-yellow-900',
    [BorrowerDtoStatusEnum.ConditionalApproval]: 'text-reskin-orange-600',
    [BorrowerDtoStatusEnum.ConditionalApprovalAppraisal]:
      'text-reskin-orange-600',
    [BorrowerDtoStatusEnum.ConditionalApprovalIncomeAssets]:
      'text-reskin-orange-600',
    [BorrowerDtoStatusEnum.ClearToClose]: 'text-reskin-purple-600',
    [BorrowerDtoStatusEnum.Closing]: 'text-reskin-green-900',
    [BorrowerDtoStatusEnum.Funding]: 'text-reskin-green-900',
    [BorrowerDtoStatusEnum.Terminated]: 'text-reskin-red-600',
  };

export const variantToBgClassNameMap: EnumMap<BorrowerDtoStatusEnum, string> = {
  [BorrowerDtoStatusEnum.AccountCreated]: 'bg-reskin-yellow-100',
  [BorrowerDtoStatusEnum.ApplicationInProgress]: 'bg-reskin-orange-100',
  [BorrowerDtoStatusEnum.ApplicationSubmitted]: 'bg-reskin-purple-100',
  [BorrowerDtoStatusEnum.LoanPreApproved]: 'bg-reskin-green-100',
  [BorrowerDtoStatusEnum.SubmittedToLender]: 'bg-zen-light-gray-1',
  [BorrowerDtoStatusEnum.Underwriting]: 'bg-yellow-100',
  [BorrowerDtoStatusEnum.ConditionalApproval]: 'bg-reskin-orange-100',
  [BorrowerDtoStatusEnum.ConditionalApprovalAppraisal]: 'bg-reskin-orange-100',
  [BorrowerDtoStatusEnum.ConditionalApprovalIncomeAssets]:
    'bg-reskin-orange-100',
  [BorrowerDtoStatusEnum.ClearToClose]: 'bg-reskin-purple-300',
  [BorrowerDtoStatusEnum.Closing]: 'bg-reskin-green-300',
  [BorrowerDtoStatusEnum.Funding]: 'bg-reskin-green-300',
  [BorrowerDtoStatusEnum.Terminated]: 'bg-reskin-red-100',
};

export const mortgageStatusIconMap: EnumMap<
  BorrowerDtoStatusEnum,
  IconDefinition
> = {
  [BorrowerDtoStatusEnum.AccountCreated]: faUserCheck,
  [BorrowerDtoStatusEnum.ApplicationInProgress]: faLoader,
  [BorrowerDtoStatusEnum.ApplicationSubmitted]: faFileCheck,
  [BorrowerDtoStatusEnum.LoanPreApproved]: faCircleCheck,
  [BorrowerDtoStatusEnum.SubmittedToLender]: faFileImport,
  [BorrowerDtoStatusEnum.Underwriting]: faListCheck,
  [BorrowerDtoStatusEnum.ConditionalApproval]: faStamp,
  [BorrowerDtoStatusEnum.ConditionalApprovalAppraisal]: faStamp,
  [BorrowerDtoStatusEnum.ConditionalApprovalIncomeAssets]: faStamp,
  [BorrowerDtoStatusEnum.ClearToClose]: faTimer,
  [BorrowerDtoStatusEnum.Closing]: faSignature,
  [BorrowerDtoStatusEnum.Funding]: faDollarSign,
  [BorrowerDtoStatusEnum.Terminated]: faXmark,
};

export const mortgageStatusToDisplayNameMap: EnumMap<
  BorrowerDtoStatusEnum,
  string
> = {
  [BorrowerDtoStatusEnum.AccountCreated]: 'Account Created',
  [BorrowerDtoStatusEnum.ApplicationInProgress]: 'Application In Progress',
  [BorrowerDtoStatusEnum.ApplicationSubmitted]: 'Application Submitted',
  [BorrowerDtoStatusEnum.LoanPreApproved]: 'Loan Pre Approved',
  [BorrowerDtoStatusEnum.SubmittedToLender]: 'Submitted To Lender',
  [BorrowerDtoStatusEnum.Underwriting]: 'Underwriting',
  [BorrowerDtoStatusEnum.ConditionalApproval]: 'Conditional Approval',
  [BorrowerDtoStatusEnum.ConditionalApprovalAppraisal]: 'Conditional Approval',
  [BorrowerDtoStatusEnum.ConditionalApprovalIncomeAssets]:
    'Conditional Approval',
  [BorrowerDtoStatusEnum.ClearToClose]: 'Clear To Close',
  [BorrowerDtoStatusEnum.Closing]: 'Closing',
  [BorrowerDtoStatusEnum.Funding]: 'Funding',
  [BorrowerDtoStatusEnum.Terminated]: 'Terminated',
};

const LoanStatusReskin: React.FC<LoanStatusProps> = ({ status }) => {
  const name = safeEnumMapGet(
    mortgageStatusToDisplayNameMap,
    status,
    capitalizeEnum(status),
  );

  const variantToBgClassName = safeEnumMapGet(
    variantToBgClassNameMap,
    status,
    variantToBgClassNameMap[BorrowerDtoStatusEnum.AccountCreated],
  );

  const variantToTextClassName = safeEnumMapGet(
    variantToTextClassNameMap,
    status,
    variantToTextClassNameMap[BorrowerDtoStatusEnum.AccountCreated],
  );

  const icon = safeEnumMapGet(
    mortgageStatusIconMap,
    status,
    mortgageStatusIconMap[BorrowerDtoStatusEnum.AccountCreated],
  );

  return (
    <div
      className={cn(
        'px-2.5 py-1.5 w-max flex justify-center items-center rounded-full text-center gap-x-2',
        variantToBgClassName,
      )}
    >
      <FontAwesomeIcon icon={icon} className={variantToTextClassName} />
      <p
        className={cn(
          'font-inter font-medium text-xs lg:text-sm',
          variantToTextClassName,
        )}
      >
        {name}
      </p>
    </div>
  );
};

export default LoanStatusReskin;
